import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpErrorResponse,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { AuthService } from '../../../modulos/auth/services/auth.service';
import { showBasicAlert } from '../../tools/swal-alerts';
import { environment } from '../../../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class LogoutInterceptor implements HttpInterceptor {
  // Bandera para evitar múltiples ejecuciones simultáneas
  private isLoggingOut = false;

  constructor(
    private readonly authService: AuthService,
    private readonly router: Router,
    private readonly dialog: MatDialog
  ) {}

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    // El servicio de "conexiones" (routing de APIs por entorno) es un
    // microservicio aparte del backend del WMS, con su propio esquema de
    // auth — un 401 suyo (ej. durante su migración de API) no significa que
    // la sesión real del WMS haya expirado. Tratarlo igual forzaba el modal
    // global de "sesión expirada" incluso justo después de un login exitoso;
    // se deja que el propio llamador (login.component) maneje ese error con
    // su mensaje habitual en vez de duplicarlo aquí.
    if (request.url.startsWith(environment.conexiones_url)) {
      return next.handle(request);
    }

    return next.handle(request).pipe(
      // Interceptar solo errores HTTP 401
      catchError((error: HttpErrorResponse) => {
        if (error.status === 401) {
          const errorResponse = error.error;
          const mensajeError =
            errorResponse?.msg || errorResponse?.message || '';
          const contieneToken =
            mensajeError.toLowerCase().includes('token') ||
            errorResponse?.error === false;

          // Solo cierra sesión el 401 de sesión inválida (endpoint auth o token
          // expirado); otros 401 se propagan sin borrar storage.
          const esEndpointAuth = /\/api\/auth\//i.test(request.url);
          const tokenExpirado = mensajeError.toLowerCase().includes('token');
          const esSesionInvalida = esEndpointAuth || tokenExpirado;

          if (!esSesionInvalida) {
            return throwError(() => error);
          }

          if (errorResponse?.error === true && !contieneToken && mensajeError) {
            this.handleLogout(mensajeError);
          } else {
            const errorMessage =
              'Su sesión ha expirado. Por favor, inicie sesión nuevamente.';
            this.handleLogout(errorMessage);
          }

          // Se propaga el error (en vez de EMPTY) para que los guards/servicios
          // que ya envuelven su `subscribe` en una Promise (ej. AutenticacionGuard)
          // reciban su callback `error` y resuelvan — con EMPTY no llega `next` ni
          // `error`, esas promesas nunca se resuelven y la navegación se cuelga
          // (pantalla en blanco en vez de completar el redirect a /auth/login).
          return throwError(() => error);
        }

        return throwError(() => error);
      })
    );
  }

  /**
   * Maneja el proceso de cierre de sesión
   */
  private handleLogout(message: string): void {
    // Evitar múltiples ejecuciones simultáneas
    if (this.isLoggingOut) {
      return;
    }

    this.isLoggingOut = true;

    this.cerrarModales();

    this.authService.logout();

    showBasicAlert('Advertencia', message, 'warning', () => {
      this.router.navigateByUrl('/auth/login');
      this.isLoggingOut = false;
    });
  }

  private cerrarModales(): void {
    try {
      const openDialogs = this.dialog.openDialogs;
      openDialogs.forEach((dialogRef) => {
        dialogRef.close();
      });
    } catch (error) {
      console.warn('Error al cerrar modales:', error);
    }
  }
}
